/**
 * Форматирование чисел и денежных сумм для UI отчёта расхождений (фича 024).
 *
 * Локаль ru-RU: неразрывный пробел между разрядами, запятая для десятичных.
 * Все возвращаемые строки предполагают рендер в `font-mono` + `tabular-nums`.
 */

/** Форматирует число (объём/количество) в локали ru-RU. */
export function fmtNum(value: number, opts?: { fractionDigits?: number }): string {
	const { fractionDigits } = opts ?? {};
	return value.toLocaleString("ru-RU", {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits ?? 2,
	});
}

/**
 * Форматирует сумму в рублях с символом ₽: «38 008 ₽».
 *
 * Округляется до целого. Для арифметической согласованности «Σ = сумма
 * отображаемых строк» родительские компоненты должны считать Σ как
 * `sum(Math.round(value))`, а не `Math.round(sum(value))`.
 */
export function fmtRub(value: number): string {
	const rounded = Math.round(value);
	return `${rounded.toLocaleString("ru-RU")} ₽`;
}

/**
 * Короткий формат суммы для значений ≥ 1 000 000: «1,45 М ₽» (миллионы)
 * или «420 К ₽» (тысячи). Иначе fallback на полный {@link fmtRub}.
 */
export function fmtRubShort(value: number): string {
	const abs = Math.abs(value);
	if (abs >= 1_000_000) {
		const millions = value / 1_000_000;
		return `${millions
			.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
			.replace(/,?0+$/, (m) => (m.startsWith(",") ? "" : m))} М ₽`;
	}
	if (abs >= 10_000) {
		const thousands = Math.round(value / 1000);
		return `${thousands.toLocaleString("ru-RU")} К ₽`;
	}
	return fmtRub(value);
}

/**
 * Форматирует период «12 → 27 марта» из массива дат.
 * Возвращает пустую строку, если массив пуст или все даты невалидны.
 */
export function formatPeriod(dates: ReadonlyArray<string | Date | null | undefined>): string {
	const parsed: Date[] = [];
	for (const d of dates) {
		if (d == null) continue;
		const dt = d instanceof Date ? d : new Date(d);
		if (!Number.isNaN(dt.getTime())) parsed.push(dt);
	}
	if (parsed.length === 0) return "";
	parsed.sort((a, b) => a.getTime() - b.getTime());
	const min = parsed[0];
	const max = parsed[parsed.length - 1];
	if (min.getTime() === max.getTime()) {
		return formatRuShortDate(min);
	}
	return `${min.getDate()} → ${formatRuShortDate(max)}`;
}

/** Форматирует дату как «27 марта». */
function formatRuShortDate(d: Date): string {
	return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}
